import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  Text,
  PrimaryButton,
  Checkbox,
  Link,
} from "@certn/cascadia-react";
import { getPrivacyPolicyUrl, getServiceAgreementUrl } from "@certn/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useClientPortalSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia";
import i18n from "../../i18n";

interface TermsAndConditionsModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
  customerGroupId?: string | null;
}

export function TermsAndConditionsModal({
  onClose,
  onSuccess,
  customerGroupId,
}: TermsAndConditionsModalProps) {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const privacyPolicyUrl = getPrivacyPolicyUrl(i18n.language);
  const serviceAgreementUrl = getServiceAgreementUrl(i18n.language);

  const { submit, isSubmitting } = useClientPortalSubmit(
    `/api/internal/client-portal/customer-accounts/${customerGroupId}/check-terms-and-conditions/` as any,
    {
      onFailure() {
        showToast(
          t("Unable to accept terms and conditions. Try again later."),
          {
            type: "error",
          }
        );
      },
      onSuccess() {
        showToast(t("Terms and conditions accepted successfully."));
        onSuccess?.();
      },
    }
  );

  const handleAcceptTerms = async (close: () => void) => {
    if (!customerGroupId || !isChecked) return;

    try {
      await submit();
      close();
      onClose?.();
    } catch {
      // Error is already handled by onFailure callback
    }
  };
  return (
    <Modal isOpen>
      <Dialog>
        {({ close }) => (
          <>
            <DialogHeader
              onClose={() => {
                close();
                onClose?.();
              }}
            >
              Agree to Certn's Terms and Conditions
            </DialogHeader>
            <DialogContent>
              <div style={styles.contentContainer}>
                <Checkbox
                  testID="terms-checkbox"
                  isSelected={isChecked}
                  onChange={setIsChecked}
                >
                  <Text as="span" style="global/body-primary">
                    By clicking confirm, I agree to be bound by Certn's{" "}
                    <Link
                      testID="terms-and-conditions-link"
                      href={serviceAgreementUrl}
                      style={styles.link}
                    >
                      Terms and Conditions
                    </Link>{" "}
                    and{" "}
                    <Link
                      testID="privacy-policy-link"
                      href={privacyPolicyUrl}
                      style={styles.link}
                    >
                      Privacy Policy
                    </Link>
                    , and I confirm that I have the authority to bind my
                    company.
                  </Text>
                </Checkbox>
              </div>
            </DialogContent>
            <DialogFooter>
              <BasicButton
                onPress={() => {
                  close();
                  onClose?.();
                }}
              >
                {t("Cancel")}
              </BasicButton>
              <PrimaryButton
                isDisabled={!isChecked || isSubmitting}
                onPress={() => handleAcceptTerms(close)}
              >
                {t("Confirm")}
              </PrimaryButton>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </Modal>
  );
}

const styles = {
  contentContainer: {
    paddingTop: "1rem",
    paddingBottom: "1.5rem",
  },
  link: {
    color: "inherit",
  },
};
